/**
 * S4 — Bulk import from the ARC/Compass AI Use Case Template (DC-1781;
 * accepted UX §7.7).
 *
 * Steps: choose file → PREVIEW (server-parsed, commits nothing) → confirm →
 * COMMIT (server re-validates; drafts only — never confirms scope, never
 * runs). The column mapping is deterministic under the versioned v4 adapter
 * and is shown for confirmation. Rejected rows render with row numbers and
 * per-row reasons and are downloadable as CSV so a bank fixes the file, not
 * the browser. Uploaded content is untrusted: every cell renders as text —
 * no HTML path exists here.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';
import { usePermissions } from '../../hooks/useAuth';
import {
  downloadDemoPackWorkbook,
  downloadImportErrorReport,
  type DemoPackLoadResult,
  type DemoPackStatus,
} from './useCaseVocabulary';
import './banking-use-cases.css';

interface PreviewRejection {
  rowNumber: number;
  useCaseRef: string | null;
  reasons: { code: string; detail: string }[];
}

interface PreviewData {
  templateVersion: string;
  columnMapping: { field: string; header: string; column: number | null; required: boolean }[];
  validRowCount: number;
  rejections: PreviewRejection[];
  skippedSyntheticExamples: { rowNumber: number; useCaseRef: string }[];
  sourceFilename: string;
}

interface CommitData {
  importId: string;
  templateVersion: string;
  importedCount: number;
  totalRows: number;
  rejections: PreviewRejection[];
  skippedSyntheticExamples: { rowNumber: number; useCaseRef: string }[];
  sourceFilename: string;
}

type Phase =
  | { step: 'choose' }
  | { step: 'previewing' }
  | { step: 'previewed'; preview: PreviewData }
  | { step: 'committing'; preview: PreviewData }
  | { step: 'done'; result: CommitData }
  | { step: 'error'; message: string };

function rejectionsCsv(rejections: PreviewRejection[]): string {
  const header = 'row_number,use_case_id,reason_code,reason_detail';
  const lines = rejections.flatMap((r) =>
    r.reasons.map(
      (reason) =>
        `${r.rowNumber},"${(r.useCaseRef ?? '').replace(/"/g, '""')}","${reason.code}","${reason.detail.replace(/"/g, '""')}"`
    )
  );
  return [header, ...lines].join('\n');
}

export const UseCaseImport: React.FC = () => {
  const { get, post, authFetch } = useApiClient();
  const { isAdmin } = usePermissions();
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>({ step: 'choose' });
  const [dragOver, setDragOver] = useState(false);
  const [demoPack, setDemoPack] = useState<DemoPackStatus | null>(null);
  const [demoBusy, setDemoBusy] = useState(false);
  const [demoMessage, setDemoMessage] = useState<string | null>(null);
  const [demoError, setDemoError] = useState<string | null>(null);

  const refreshDemoPack = useCallback(async () => {
    // Absence of the status just means no demo section renders.
    try {
      const envelope = await get<{ data?: { demoPack?: DemoPackStatus } }>(
        '/api/use-cases/demo-pack'
      );
      setDemoPack(envelope.data?.demoPack ?? null);
    } catch {
      setDemoPack(null);
    }
  }, [get]);

  useEffect(() => {
    void refreshDemoPack();
  }, [refreshDemoPack]);

  const loadDemoPackNow = useCallback(async () => {
    setDemoBusy(true);
    setDemoError(null);
    setDemoMessage(null);
    try {
      const envelope = await post<{ data?: { load?: DemoPackLoadResult } }>(
        '/api/use-cases/demo-pack/load',
        {}
      );
      const load = envelope.data?.load;
      if (load?.alreadyLoaded) {
        setDemoMessage('The demo pack is already fully loaded — nothing was changed.');
      } else {
        setDemoMessage(
          `Loaded ${load?.importedCount ?? 0} synthetic use case${
            (load?.importedCount ?? 0) === 1 ? '' : 's'
          } as drafts, with provisional ARC identifier suggestions where the ruleset applies.`
        );
      }
      await refreshDemoPack();
    } catch {
      setDemoError('The demo pack could not be loaded. The inventory was not changed.');
    } finally {
      setDemoBusy(false);
    }
  }, [post, refreshDemoPack]);

  async function callImport(path: string): Promise<Response> {
    const form = new FormData();
    form.append('file', file as File);
    return authFetch(path, { method: 'POST', body: form });
  }

  async function readError(res: Response): Promise<string> {
    try {
      const body = (await res.json()) as { error?: { message?: string } };
      return body.error?.message ?? 'The upload could not be processed.';
    } catch {
      return 'The upload could not be processed.';
    }
  }

  async function preview(): Promise<void> {
    if (!file) return;
    setPhase({ step: 'previewing' });
    try {
      const res = await callImport('/api/use-cases/import/preview');
      if (!res.ok) {
        setPhase({ step: 'error', message: await readError(res) });
        return;
      }
      const body = (await res.json()) as { data: PreviewData };
      setPhase({ step: 'previewed', preview: body.data });
    } catch {
      setPhase({ step: 'error', message: 'The upload could not be processed.' });
    }
  }

  async function commit(preview: PreviewData): Promise<void> {
    setPhase({ step: 'committing', preview });
    try {
      const res = await callImport('/api/use-cases/import');
      if (!res.ok) {
        setPhase({ step: 'error', message: await readError(res) });
        return;
      }
      const body = (await res.json()) as { data: CommitData };
      setPhase({ step: 'done', result: body.data });
    } catch {
      setPhase({ step: 'error', message: 'The import was not committed.' });
    }
  }

  function downloadRejections(rejections: PreviewRejection[], filename: string): void {
    const blob = new Blob([rejectionsCsv(rejections)], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename.replace(/\.[^.]+$/, '')}-rejections.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  /** Both intake paths — the input and the drop zone — hand the file to one setter. */
  function acceptFiles(files: FileList | null | undefined): void {
    setFile(files && files.length > 0 ? files[0] : null);
  }

  const rejectionTable = (rejections: PreviewRejection[]): React.ReactElement | null =>
    rejections.length === 0 ? null : (
      <table className="banking-grid banking-ledger banking-rejections">
        <thead>
          <tr>
            <th scope="col">Row</th>
            <th scope="col">Use Case ID</th>
            <th scope="col">Why it was rejected</th>
          </tr>
        </thead>
        <tbody>
          {rejections.map((r) => (
            <tr key={`${r.rowNumber}`} className="banking-ledger-warn">
              <td>{r.rowNumber}</td>
              <td>{r.useCaseRef ?? '—'}</td>
              <td className="banking-ledger-reason">
                <ul>
                  {r.reasons.map((reason) => (
                    <li key={reason.code + reason.detail}>{reason.detail}</li>
                  ))}
                </ul>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );

  return (
    <div className="banking-page use-case-import">
      <nav className="banking-breadcrumb" aria-label="Breadcrumb">
        <Link to="/use-cases">AI Use Cases</Link> › Import from template
      </nav>
      <header className="banking-page-header banking-header-editorial">
        <div className="banking-header-lede">
          <p className="banking-eyebrow">BULK IMPORT</p>
          <h1 className="banking-title banking-title-editorial">Import AI use cases</h1>
          <p className="banking-subtitle">
            Upload a completed ARC/Compass AI Use Case Template (v4). Every row is
            validated before anything commits; committing creates drafts only — it never
            confirms scope and never runs an assessment.
          </p>
        </div>
      </header>

      {(phase.step === 'choose' ||
        phase.step === 'previewing' ||
        phase.step === 'error') && (
        <div className="banking-import-choose">
          {/* Drop is an accelerator, never the only path: the file input below
              stays visible so keyboard and AT users have the same intake. */}
          <div
            className={`banking-dropzone${dragOver ? ' is-dragover' : ''}`}
            onDragEnter={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              acceptFiles(e.dataTransfer?.files);
            }}
          >
            <p className="banking-dropzone-lede">Drop your template here</p>
            <p className="banking-dropzone-note">
              A single .xlsx workbook on the v4 template. Nothing uploads until you
              choose Preview.
            </p>
            <label className="banking-file-label banking-dropzone-input">
              Template file (.xlsx)
              <input
                type="file"
                accept=".xlsx"
                data-testid="import-file-input"
                onChange={(e) => acceptFiles(e.target.files)}
              />
            </label>
            {file && <p className="banking-dropzone-file">Selected: {file.name}</p>}
          </div>
          <div className="banking-stage-nav">
            <button
              type="button"
              className="banking-btn banking-btn-primary"
              disabled={!file || phase.step === 'previewing'}
              onClick={() => void preview()}
            >
              {phase.step === 'previewing' ? 'Validating…' : 'Preview'}
            </button>
          </div>
          {phase.step === 'error' && (
            <div className="banking-create-error" data-testid="import-error" role="alert">
              {phase.message}
            </div>
          )}
        </div>
      )}

      {(phase.step === 'previewed' || phase.step === 'committing') && (
        <section className="banking-import-preview" data-testid="import-preview">
          <h2>
            Validation preview — {phase.preview.sourceFilename} · template v
            {phase.preview.templateVersion}
          </h2>
          <p>
            <strong>{phase.preview.validRowCount}</strong> row
            {phase.preview.validRowCount === 1 ? '' : 's'} will be imported as drafts.{' '}
            {phase.preview.rejections.length > 0 && (
              <>
                <strong>{phase.preview.rejections.length}</strong> row
                {phase.preview.rejections.length === 1 ? '' : 's'} will be rejected.
              </>
            )}
            {phase.preview.skippedSyntheticExamples.length > 0 && (
              <>
                {' '}
                {phase.preview.skippedSyntheticExamples.length} clearly-labeled synthetic
                example row
                {phase.preview.skippedSyntheticExamples.length === 1 ? '' : 's'} will be
                skipped.
              </>
            )}
          </p>
          {rejectionTable(phase.preview.rejections)}
          {phase.preview.rejections.length > 0 && (
            <button
              type="button"
              className="banking-btn banking-btn-secondary"
              onClick={() =>
                downloadRejections(phase.preview.rejections, phase.preview.sourceFilename)
              }
            >
              Download rejected rows (CSV)
            </button>
          )}
          <div className="banking-stage-nav">
            <button
              type="button"
              className="banking-btn banking-btn-secondary"
              onClick={() => setPhase({ step: 'choose' })}
            >
              Choose a different file
            </button>
            <button
              type="button"
              className="banking-btn banking-btn-primary"
              disabled={phase.step === 'committing' || phase.preview.validRowCount === 0}
              onClick={() => void commit(phase.preview)}
            >
              {phase.step === 'committing'
                ? 'Importing…'
                : `Import ${phase.preview.validRowCount} use case${
                    phase.preview.validRowCount === 1 ? '' : 's'
                  }`}
            </button>
          </div>
        </section>
      )}

      {phase.step === 'choose' && demoPack && (
        <section className="banking-demo-pack-card" data-testid="demo-pack-section">
          <p className="banking-eyebrow">DEMO PACK · SYNTHETIC</p>
          <h2>Credit Scoring demo pack</h2>
          <p>{demoPack.label}</p>
          <p>
            {demoPack.loaded
              ? `All ${demoPack.caseCount} synthetic cases are already in this inventory.`
              : `${demoPack.presentCount} of ${demoPack.caseCount} synthetic cases are in this inventory.`}{' '}
            The workbook is a real v4 template file — download it to walk the
            upload path above with synthetic content, or load the pack
            directly through the same import contract.
          </p>
          {demoMessage && (
            <div className="banking-import-banner" data-testid="demo-pack-message" role="status">
              {demoMessage}
            </div>
          )}
          {demoError && (
            <div className="banking-create-error" data-testid="demo-pack-error" role="alert">
              {demoError}
            </div>
          )}
          <div className="banking-stage-nav">
            <button
              type="button"
              className="banking-btn banking-btn-secondary"
              data-testid="demo-pack-download"
              onClick={() => void downloadDemoPackWorkbook(authFetch)}
            >
              Download demo workbook (.xlsx)
            </button>
            {isAdmin && !demoPack.loaded && (
              <button
                type="button"
                className="banking-btn banking-btn-primary"
                data-testid="demo-pack-load"
                disabled={demoBusy}
                onClick={() => void loadDemoPackNow()}
              >
                {demoBusy ? 'Loading demo pack…' : 'Load demo pack'}
              </button>
            )}
          </div>
        </section>
      )}

      {phase.step === 'done' && (
        <section className="banking-import-result" data-testid="import-result">
          <h2>Import complete — template v{phase.result.templateVersion}</h2>
          <p>
            <strong>{phase.result.importedCount}</strong> of {phase.result.totalRows} row
            {phase.result.totalRows === 1 ? '' : 's'} imported as drafts from{' '}
            {phase.result.sourceFilename}.
          </p>
          {rejectionTable(phase.result.rejections)}
          {phase.result.rejections.length > 0 && (
            <button
              type="button"
              className="banking-btn banking-btn-secondary"
              data-testid="import-error-report"
              onClick={() => void downloadImportErrorReport(authFetch, phase.result.importId)}
            >
              Download error report (CSV)
            </button>
          )}
          <Link to="/use-cases" className="banking-btn banking-btn-primary">
            Go to the inventory
          </Link>
        </section>
      )}
    </div>
  );
};

export default UseCaseImport;
